/**
 * BoardSuiteInviteModal, per-property Charter Suite kickoff from the
 * Portfolio board. Two jobs, in priority order:
 *   1. Welcome the owner (primary): send the founder Welcome letter to the
 *      property's contact on record (shown, not blind). Their Charter Suite
 *      login link follows automatically ~30 min later via the scheduled
 *      sweep, so the owner needs no separate invite.
 *   2. Invite another manager (optional, secondary): give a DIFFERENT person
 *      their own Suite access, sent to the email typed here, not to the
 *      property contact.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { formatDateTime } from '../../../../../utils/formatters';
import { useToast } from '../../../../../components/Toast';

interface Props {
  supabase: SupabaseClient;
  propertyId: string;
  propertyName: string;
  contactEmail: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface InviteResult {
  url: string;
  expires_at: string | null;
  emailSent: boolean;
  emailWarning: string | null;
}

export default function BoardSuiteInviteModal({
  supabase, propertyId, propertyName, contactEmail, contactFirstName, contactLastName, onClose, onSaved,
}: Props) {
  const { showToast } = useToast();

  // Welcome (primary)
  const [welcomeTo, setWelcomeTo] = useState(contactEmail || '');
  const [welcomeToName, setWelcomeToName] = useState(contactFirstName || '');
  // CC on the welcome email only. Pre-filled from the server (assigned manager
  // + the standing oversight addresses), editable as a comma/line-separated
  // list. The login link and onboarding series are not affected.
  const [welcomeCc, setWelcomeCc] = useState('');
  // Default sender name (the founder), resolved server-side so the name is not
  // baked into the app bundle. Labels the default "Send as" option.
  const [defaultSenderName, setDefaultSenderName] = useState('');
  const [charterMode, setCharterMode] = useState<'couriered' | 'presented'>('couriered');
  const [presentedBy, setPresentedBy] = useState('');
  const [sendAsSelf, setSendAsSelf] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [welcomeSent, setWelcomeSent] = useState(false);

  // Invite another manager (optional)
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteResult | null>(null);

  const contactName = `${contactFirstName || ''} ${contactLastName || ''}`.trim();
  const canSendWelcome = !!welcomeTo.trim();

  const parseCcList = (raw: string): string[] =>
    Array.from(new Set(raw.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)));

  // Pre-fill the CC field on open with the property's server-resolved defaults
  // (assigned manager + oversight). Best-effort: on failure the operator types.
  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase.functions.invoke('pos-charter-suite-send-welcome', {
          body: { property_id: propertyId, defaults_only: true },
        });
        const cc = (data as { default_cc?: string[] })?.default_cc;
        if (active && Array.isArray(cc) && cc.length) setWelcomeCc(cc.join(', '));
        const senderName = (data as { default_sender_name?: string })?.default_sender_name;
        if (active && senderName) setDefaultSenderName(senderName);
      } catch { /* operator can add the CC by hand */ }
    })();
    return () => { active = false; };
  }, [propertyId, supabase]);

  async function loadPreview() {
    if (!welcomeTo.trim()) { showToast('Add a recipient email first.', 'error'); return; }
    setPreviewing(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-charter-suite-send-welcome', {
        body: { property_id: propertyId, charter_mode: charterMode, presented_by: presentedBy.trim() || undefined, send_as_self: sendAsSelf, recipient: welcomeTo.trim(), recipient_name: welcomeToName.trim() || undefined, cc: parseCcList(welcomeCc), preview_only: true },
      });
      const html = (data as { preview_html?: string })?.preview_html;
      if (invokeError || !html) throw new Error(invokeError?.message ?? (data as { reason?: string })?.reason ?? 'Preview failed.');
      setPreviewHtml(html);
    } catch (err: unknown) {
      showToast(`Preview failed: ${err instanceof Error ? err.message : 'unknown error'}`, 'error');
    } finally {
      setPreviewing(false);
    }
  }

  async function sendWelcome() {
    if (!welcomeTo.trim()) { showToast('Add a recipient email for the welcome.', 'error'); return; }
    setWelcomeSending(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-charter-suite-send-welcome', {
        body: { property_id: propertyId, charter_mode: charterMode, presented_by: presentedBy.trim() || undefined, send_as_self: sendAsSelf, recipient: welcomeTo.trim(), recipient_name: welcomeToName.trim() || undefined, cc: parseCcList(welcomeCc) },
      });
      if (invokeError || (data as { error?: string })?.error || !(data as { success?: boolean })?.success) {
        throw new Error(invokeError?.message ?? (data as { error?: string })?.error ?? (data as { reason?: string })?.reason ?? 'Failed to send Welcome.');
      }
      setWelcomeSent(true);
      showToast(`Welcome email sent for ${propertyName}.`, 'success');
    } catch (err: unknown) {
      showToast(`Welcome send failed: ${err instanceof Error ? err.message : 'unknown error'}`, 'error');
    } finally {
      setWelcomeSending(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) { setError('Email is required.'); return; }
    setSending(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-charter-suite-invite', {
        body: {
          email: email.trim().toLowerCase(),
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
          group_id: null,
          property_ids: [propertyId],
          access_level: 'manager',
        },
      });
      if (invokeError || !data?.success) {
        setError(invokeError?.message ?? data?.reason ?? 'Failed to create invite.');
        setSending(false);
        return;
      }

      // Actually send the branded invite email.
      let emailSent = false;
      let emailWarning: string | null = null;
      try {
        const { data: emailData, error: emailErr } = await supabase.functions.invoke('pos-charter-suite-send-invite-email', {
          body: { invite_id: data.invite_id },
        });
        emailSent = !emailErr && !!emailData?.success;
        if (!emailSent) emailWarning = emailErr?.message ?? emailData?.reason ?? 'The invite was created but the email did not send.';
      } catch (emailEx) {
        emailWarning = emailEx instanceof Error ? emailEx.message : 'The invite was created but the email did not send.';
      }

      setResult({ url: data.url, expires_at: data.expires_at ?? null, emailSent, emailWarning });
      showToast(emailSent ? `Invite emailed for ${propertyName}.` : `Invite created for ${propertyName} (email not sent).`, emailSent ? 'success' : 'info');
      await onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create invite.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1100 }}>
      <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Charter Suite: {propertyName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body modal-body--scroll">

          {/* 1. WELCOME THE OWNER (primary) */}
          <div className="form-section">
            <h3 className="modal-section-heading">Welcome the owner</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Send the welcome to</label>
                <input
                  type="email"
                  className="form-input"
                  value={welcomeTo}
                  onChange={(e) => { setWelcomeTo(e.target.value); setPreviewHtml(null); }}
                  placeholder="Recipient email"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Recipient name</label>
                <input
                  type="text"
                  className="form-input"
                  value={welcomeToName}
                  onChange={(e) => { setWelcomeToName(e.target.value); setPreviewHtml(null); }}
                  placeholder="For the greeting"
                />
              </div>
            </div>
            <p className="text-muted fs-xs mb-md">
              Defaults to the property contact{contactName ? `, ${contactName}` : ''}. Change them if the welcome should go to someone else. With no name the letter opens with &quot;Hello&quot;. Their Suite login link then follows on its own about 30 minutes later, to this same address. The wider onboarding email series will be added here once it is ready.
            </p>
            <div className="form-group">
              <label className="form-label">CC on the welcome</label>
              <textarea
                className="form-input"
                rows={2}
                value={welcomeCc}
                onChange={(e) => setWelcomeCc(e.target.value)}
                placeholder="Copy others on the welcome email"
              />
              <p className="text-muted fs-xs mt-xs">
                Pre-filled with the property&apos;s manager and the Peace of Sleep team. Add or remove addresses, separated by commas. This copies the welcome email only, not the Suite login link.
              </p>
            </div>
            <div className="form-group">
              <label className="form-label">Framed Charter delivery</label>
              <div className="form-row">
                <button
                  type="button"
                  className={`btn w-full ${charterMode === 'couriered' ? 'btn-primary btn-pos-gold' : 'btn-secondary'}`}
                  onClick={() => { setCharterMode('couriered'); setPreviewHtml(null); }}
                >
                  Couriered shortly
                </button>
                <button
                  type="button"
                  className={`btn w-full ${charterMode === 'presented' ? 'btn-primary btn-pos-gold' : 'btn-secondary'}`}
                  onClick={() => { setCharterMode('presented'); setPreviewHtml(null); }}
                >
                  Presented in person
                </button>
              </div>
            </div>
            {charterMode === 'presented' && (
              <div className="form-group">
                <label className="form-label">Presented by</label>
                <input type="text" className="form-input" placeholder="Name" value={presentedBy} onChange={(e) => { setPresentedBy(e.target.value); setPreviewHtml(null); }} />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Send as</label>
              <div className="form-row">
                <button
                  type="button"
                  className={`btn w-full ${!sendAsSelf ? 'btn-primary btn-pos-gold' : 'btn-secondary'}`}
                  onClick={() => { setSendAsSelf(false); setPreviewHtml(null); }}
                >
                  {defaultSenderName || 'Peace of Sleep team'}
                </button>
                <button
                  type="button"
                  className={`btn w-full ${sendAsSelf ? 'btn-primary btn-pos-gold' : 'btn-secondary'}`}
                  onClick={() => { setSendAsSelf(true); setPreviewHtml(null); }}
                >
                  Send as me
                </button>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-secondary w-full"
              onClick={() => void loadPreview()}
              disabled={previewing || !canSendWelcome}
            >
              {previewing ? 'Loading preview…' : previewHtml ? 'Refresh preview' : 'Preview email'}
            </button>
            {previewHtml && (
              <div className="preview-container mt-sm">
                <iframe srcDoc={previewHtml} className="w-full border-none cl-preview-iframe" title="Welcome email preview" />
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary btn-pos-gold w-full mt-sm"
              onClick={() => void sendWelcome()}
              disabled={welcomeSending || welcomeSent || !canSendWelcome}
            >
              {welcomeSending ? 'Sending…' : welcomeSent ? '✓ Welcome sent' : 'Send Welcome email'}
            </button>
          </div>

          {/* 2. INVITE ANOTHER MANAGER (optional) */}
          <div className="form-section">
            <h3 className="modal-section-heading">Invite another manager (optional)</h3>
            {result ? (
              <>
                <p className="fs-sm">
                  {result.emailSent
                    ? 'Invite created and the branded email has been sent.'
                    : 'Invite created. The email did not send, share the link below instead.'}
                </p>
                {!result.emailSent && result.emailWarning && (
                  <div className="alert alert-error fs-xs mb-md">{result.emailWarning}</div>
                )}
                <div className="form-group">
                  <label className="form-label">Invite link</label>
                  <input className="form-input" type="text" value={result.url} readOnly />
                </div>
                {result.expires_at && (
                  <p className="text-muted fs-xs">Link expires {formatDateTime(result.expires_at)}.</p>
                )}
              </>
            ) : (
              <form onSubmit={handleSubmit}>
                <p className="text-muted fs-xs mb-md">
                  Only if someone other than the owner will run the Suite. This sends a separate Charter Suite invitation to the email you enter here, not to the contact above. Access level is Manager, scoped to this property; for a Group manager use the Charters, Suite Invites page.
                </p>
                <div className="form-group">
                  <label className="form-label">Email <span className="required">*</span></label>
                  <input type="email" className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First name</label>
                    <input type="text" className="form-input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last name</label>
                    <input type="text" className="form-input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                {error && <div className="form-error mb-md">{error}</div>}
                <button type="submit" className="btn btn-secondary w-full" disabled={sending}>
                  {sending ? 'Sending…' : 'Send invite to this person'}
                </button>
              </form>
            )}
          </div>

        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending || welcomeSending}>Close</button>
        </div>
      </div>
    </div>
  );
}
